import React from 'react';
import { Formik, Form } from 'formik';
import { object, date, number } from 'yup';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import { UpliftIndexRate } from '@/graphql/contracts/_generated_/contracts';
import { NumberUtils } from '@/utils/numbers.utils';

type Values = {
  rate: string;
  effectiveDate: string | Date | null;
};

interface RateModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  initialRate?: UpliftIndexRate | null;
  onSubmit: (values: { rate: number; effectiveDate: Date }) => Promise<void>;
  loading?: boolean;
}

const validationSchema = object().shape({
  rate: number().typeError('Rate must be a number')
    .required('Rate is required')
    .max(100, 'Rate cannot be more than 100')
    .min(-100, 'Rate cannot be less than 0'),
  effectiveDate: date().typeError('Effective Date is required').required('Effective Date is required'),
});

export default function RateModal({
  isOpen,
  onClose,
  title,
  initialRate,
  onSubmit,
  loading,
}: RateModalProps) {
  if (!isOpen) return null;

  const initialValues: Values = {
    rate: initialRate?.rate?.toString() 
      ? NumberUtils.prettifyWithRound(parseFloat(initialRate?.rate?.toString() ?? 0) * 100) 
      : '',
    effectiveDate: initialRate?.effectiveDate ? new Date(initialRate.effectiveDate) : null,
  };

  return (
    <AgModalWindow isOpen={isOpen} onClose={onClose} title={title} contentStyle={{ width: 340 }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await onSubmit({
              rate: Number(values.rate),
              effectiveDate: values.effectiveDate ? new Date(values.effectiveDate) : new Date(),
            });
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <ModalFormTextInput
              id="rate"
              labelTitle="Rate"
              requiredIcon
              value={values.rate}
              onChange={(e) => setFieldValue('rate', e.target.value)}
              validationMessage={touched.rate ? (errors.rate as string) : undefined}
            />

            <div className="mt-10" />

            <ModalFormDatepicker
              id="effectiveDate"
              labelTitle="Effective Date"
              requiredIcon
              value={values.effectiveDate ? new Date(values.effectiveDate) : null}
              onChange={({ value }) => setFieldValue('effectiveDate', value)}
              validationMessage={touched.effectiveDate ? (errors.effectiveDate as string) : undefined}
            />

            <div className="mt-20" />

            <ActionButtonsWrapper
              leftButton={(
                <Button buttonStyleType={ButtonStyleType.SECONDARY} onClick={onClose}>
                  Cancel
                </Button>
              )}
              rightButton={(
                <Button buttonStyleType={ButtonStyleType.PRIMARY} type="submit" disabled={isSubmitting || !!loading}>
                  Save
                </Button>
              )}
            />
          </Form>
        )}
      </Formik>
    </AgModalWindow>
  );
}
